import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampaignCreateSchema,
  type CampaignAssigneeKind,
  type CampaignChannel,
  type RecurrenceRule,
} from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input, Select } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';
import { RecurrenceEditor } from '../components/recurrence-editor.js';
import { AssigneePicker } from '../components/assignee-picker.js';

const ALL_CHANNELS: { value: CampaignChannel; label: string }[] = [
  { value: 'auto_dialer', label: 'Auto-dialer' },
  { value: 'ai_message', label: 'AI message (TTS)' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

interface FormState {
  tag: string;
  recipient_name: string;
  recipient_email: string;
  recipient_phone: string;
  start_at: string;
  contact_profile_id: string;
  message_template_id: string;
  channels: CampaignChannel[];
  recurrence: RecurrenceRule | null;
  /** '' = no assignee → ai_message TTS direct. */
  assignee_kind: '' | CampaignAssigneeKind;
  assignee_value: string;
}

const EMPTY: FormState = {
  tag: '',
  recipient_name: '',
  recipient_email: '',
  recipient_phone: '',
  start_at: '',
  contact_profile_id: '',
  message_template_id: '',
  channels: ['ai_message'],
  recurrence: null,
  assignee_kind: '',
  assignee_value: '',
};

interface ListResp<T> { data: T[]; pagination: unknown }
interface ContactProfileLite { id: string; name: string }
interface MessageTemplateLite { id: string; name: string }

export function NewCampaignPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const profilesQ = useQuery({
    queryKey: ['contact-profiles'],
    queryFn: () => api.get('/v1/contact-profiles') as Promise<ListResp<ContactProfileLite>>,
  });
  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => api.get('/v1/message-templates') as Promise<ListResp<MessageTemplateLite>>,
  });

  const create = useMutation({
    mutationFn: (payload: unknown) =>
      api.post('/v1/campaigns', payload) as Promise<{ id: string }>,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      if (created?.id) navigate(`/campaigns/${created.id}/edit`);
      else navigate('/campaigns');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const update = <K extends keyof FormState>(key: K) => (value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };
  const toggleChannel = (c: CampaignChannel): void => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c],
    }));
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (create.isPending) return; // double-save guard
    setBannerError(null);

    const payload: Record<string, unknown> = {
      tag: form.tag.trim(),
      recipient_phone: form.recipient_phone.trim(),
      start_at: form.start_at ? new Date(form.start_at).toISOString() : '',
      channels: form.channels,
    };
    if (form.recipient_name.trim()) payload['recipient_name'] = form.recipient_name.trim();
    if (form.recipient_email.trim()) payload['recipient_email'] = form.recipient_email.trim();
    if (form.contact_profile_id) payload['contact_profile_id'] = form.contact_profile_id;
    if (form.message_template_id) payload['message_template_id'] = form.message_template_id;
    if (form.recurrence) payload['recurrence'] = form.recurrence;
    if (form.assignee_kind && form.assignee_value.trim()) {
      payload['assignee_kind'] = form.assignee_kind;
      payload['assignee_value'] = form.assignee_value.trim();
    }

    const fieldIssues = fieldErrors(CampaignCreateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    create.mutate(payload);
  };

  const profiles = useMemo(() => profilesQ.data?.data ?? [], [profilesQ.data]);
  const templates = useMemo(() => templatesQ.data?.data ?? [], [templatesQ.data]);

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">New campaign</h1>
        <p className="text-sm text-ink-muted">
          Outbound message that fires once at <code>start_at</code> across the selected channels.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Campaign</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Tag" required hint="Display label for this campaign" error={errors?.['tag']}>
              {(id) => (
                <Input id={id} value={form.tag} onChange={(e) => update('tag')(e.target.value)} required autoFocus />
              )}
            </FormField>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Recipient</legend>
              <FormField label="Name" error={errors?.['recipient_name']}>
                {(id) => (
                  <Input
                    id={id}
                    value={form.recipient_name}
                    onChange={(e) => update('recipient_name')(e.target.value)}
                  />
                )}
              </FormField>
              <div className="grid grid-cols-2 gap-4">
                <FormField label="Phone" required hint="E.164" error={errors?.['recipient_phone']}>
                  {(id) => (
                    <Input
                      id={id}
                      value={form.recipient_phone}
                      onChange={(e) => update('recipient_phone')(e.target.value)}
                      required
                      placeholder="+15555550100"
                    />
                  )}
                </FormField>
                <FormField label="Email" error={errors?.['recipient_email']}>
                  {(id) => (
                    <Input
                      id={id}
                      type="email"
                      value={form.recipient_email}
                      onChange={(e) => update('recipient_email')(e.target.value)}
                    />
                  )}
                </FormField>
              </div>
            </fieldset>

            <FormField label="Start" required error={errors?.['start_at']}>
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(e) => update('start_at')(e.target.value)}
                  required
                />
              )}
            </FormField>

            <RecurrenceEditor
              value={form.recurrence}
              onChange={(r) => setForm((f) => ({ ...f, recurrence: r }))}
              errors={errors}
            />

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Assigned to</legend>
              <p className="text-xs text-ink-muted">
                Set for <strong>auto-dialer</strong> campaigns: ring this internal extension
                or call-queue first, then live-bridge to the external party (no TTS).
                Leave blank for AI/TTS campaigns — the recipient hears the configured
                message directly.
              </p>
              <div className="grid grid-cols-[10rem_1fr] gap-3">
                <Select
                  value={form.assignee_kind}
                  onChange={(e) =>
                    update('assignee_kind')(e.target.value as '' | CampaignAssigneeKind)
                  }
                >
                  <option value="">— None (AI/TTS) —</option>
                  <option value="extension">Extension</option>
                  <option value="callgroup">Call queue</option>
                </Select>
                <AssigneePicker
                  kind={form.assignee_kind}
                  value={form.assignee_value}
                  onChange={(v) => update('assignee_value')(v)}
                />
              </div>
              {errors?.['assignee_value'] && (
                <p className="text-xs text-danger">{errors['assignee_value']}</p>
              )}
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-2">
              <legend className="px-1 text-sm font-medium text-ink-muted">Channels</legend>
              {errors?.['channels'] && <p className="text-xs text-danger">{errors['channels']}</p>}
              <div className="grid grid-cols-2 gap-2">
                {ALL_CHANNELS.map((c) => (
                  <label key={c.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.channels.includes(c.value)}
                      onChange={() => toggleChannel(c.value)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="grid grid-cols-2 gap-4">
              <FormField label="Contact profile" error={errors?.['contact_profile_id']}>
                {(id) => (
                  <Select id={id} value={form.contact_profile_id} onChange={(e) => update('contact_profile_id')(e.target.value)}>
                    <option value="">— None —</option>
                    {profiles.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField label="Message template" error={errors?.['message_template_id']}>
                {(id) => (
                  <Select id={id} value={form.message_template_id} onChange={(e) => update('message_template_id')(e.target.value)}>
                    <option value="">— None —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </Select>
                )}
              </FormField>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/campaigns')}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create campaign'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
